import { useEffect, useRef } from "react";

import { AuthStatus } from "@/lib/constant";
import { isNativeApp, nativeBridge } from "@/lib/native";
import { useAuthStore } from "@/store/authStore";

const PUBLIC_PATHS = ["/privacy", "/terms", "/support"];

function isPublicPath(pathname: string) {
  return PUBLIC_PATHS.some(path => pathname === path || pathname.startsWith(`${path}/`));
}

function isReadyToReveal(status: AuthStatus, pathname: string) {
  if (status === AuthStatus.AUTHENTICATED) {
    return pathname !== "/login";
  }

  if (status === AuthStatus.UNAUTHENTICATED) {
    return pathname === "/login" || isPublicPath(pathname);
  }

  return false;
}

export function NativeSplashController() {
  const status = useAuthStore(state => state.status);
  const hasRequestedHide = useRef(false);

  useEffect(() => {
    if (!isNativeApp || status === AuthStatus.LOADING || hasRequestedHide.current) {
      return;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let firstFrameId: number | undefined;
    let secondFrameId: number | undefined;

    const hideWhenPainted = () => {
      hasRequestedHide.current = true;
      firstFrameId = window.requestAnimationFrame(() => {
        secondFrameId = window.requestAnimationFrame(() => {
          void nativeBridge.system.hideSplash();
        });
      });
    };

    const waitForRoute = () => {
      if (cancelled || hasRequestedHide.current) {
        return;
      }

      if (isReadyToReveal(status, window.location.pathname)) {
        hideWhenPainted();
        return;
      }

      timeoutId = window.setTimeout(waitForRoute, 50);
    };

    waitForRoute();

    return () => {
      cancelled = true;

      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }

      if (firstFrameId !== undefined) {
        window.cancelAnimationFrame(firstFrameId);
      }

      if (secondFrameId !== undefined) {
        window.cancelAnimationFrame(secondFrameId);
      }
    };
  }, [status]);

  return null;
}
